import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  alpha,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { DashboardListItem, DrilldownKind } from '../../../interfaces/IDashboard'
import { bucketColor } from './BucketColors'
import { useEventTypeColor } from '../../../hooks/useEventTypeColor'

interface EntityListDialogProps {
  open: boolean
  onClose: () => void
  title: string
  kind: DrilldownKind | null
}

const CRITICALITY_TOKENS = new Set(['A', 'B', 'C', 'Lav', 'Medium', 'Høy'])
const TYPE_TOKENS = new Set(['Fellesfeil', 'Planlagt', 'Generell'])

interface ParsedSegment {
  text: string
  role: 'criticality' | 'type' | 'other'
}

function parseSegments(text: string): ParsedSegment[] {
  return text
    .split(' · ')
    .map((s) => s.trim())
    .filter(Boolean)
    .map<ParsedSegment>((s) => {
      if (CRITICALITY_TOKENS.has(s)) return { text: s, role: 'criticality' }
      if (TYPE_TOKENS.has(s)) return { text: s, role: 'type' }
      return { text: s, role: 'other' }
    })
}

function pickAccentColor(segments: ParsedSegment[], typeColor: (t: string) => string): string | null {
  const crit = segments.find((s) => s.role === 'criticality')
  if (crit) return bucketColor(crit.text)
  const type = segments.find((s) => s.role === 'type')
  if (type) return typeColor(type.text)
  return null
}

function EntityRow({ item, isMobile }: { item: DashboardListItem; isMobile: boolean }) {
  const typeColor = useEventTypeColor()
  const segments = parseSegments(item.secondaryText)
  const accent = pickAccentColor(segments, typeColor)

  const meta = segments.map((seg, idx) => {
    const isLast = idx === segments.length - 1
    const separator = isLast ? null : (
      <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>
        ·
      </Box>
    )
    if (seg.role === 'criticality') {
      const bg = bucketColor(seg.text)
      return (
        <Box component="span" key={`${seg.text}-${idx}`}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              minWidth: 20,
              textAlign: 'center',
              px: 0.75,
              py: 0.1,
              borderRadius: 0.75,
              backgroundColor: alpha(bg, 0.18),
              color: bg,
              fontWeight: 700,
              fontSize: '0.72rem',
              lineHeight: 1.4,
            }}
          >
            {seg.text}
          </Box>
          {separator}
        </Box>
      )
    }
    if (seg.role === 'type') {
      return (
        <Box component="span" key={`${seg.text}-${idx}`}>
          <Box component="span" sx={{ color: typeColor(seg.text), fontWeight: 600 }}>
            {seg.text}
          </Box>
          {separator}
        </Box>
      )
    }
    return (
      <Box component="span" key={`${seg.text}-${idx}`} sx={{ color: 'text.secondary' }}>
        {seg.text}
        {separator}
      </Box>
    )
  })

  return (
    <Stack
      direction="row"
      spacing={isMobile ? 1 : 1.5}
      sx={{
        alignItems: 'flex-start',
        width: '100%',
        py: isMobile ? 1.25 : 1,
        pl: isMobile ? 1 : 1.25,
        pr: isMobile ? 1 : 1.5,
        borderLeft: isMobile ? '4px solid' : '3px solid',
        borderLeftColor: accent ?? 'transparent',
        minHeight: isMobile ? 48 : undefined,
      }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.primaryText}
        </Typography>
        {segments.length > 0 && (
          <Typography variant="caption" component="div" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {meta}
          </Typography>
        )}
        {isMobile && item.tertiaryText && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {item.tertiaryText}
          </Typography>
        )}
      </Stack>
      {!isMobile && item.tertiaryText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', flexShrink: 0, fontStyle: 'italic', alignSelf: 'center' }}
        >
          {item.tertiaryText}
        </Typography>
      )}
    </Stack>
  )
}

function EntityListDialog({ open, onClose, title, kind }: EntityListDialogProps) {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { context } = useContext(EbAuthenticatedAppContext)
  const [items, setItems] = useState<DashboardListItem[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!open || !kind) return
    let mounted = true
    setItems(null)
    setError(false)
    context.restApiClient
      .buildRequest(`/api/stats/dashboard/drilldown?kind=${encodeURIComponent(kind)}`)
      .deserializeResponseToJson<DashboardListItem[]>()
      .getAsync()
      .then((response) => {
        if (!mounted) return
        if (response.json) setItems(response.json)
        else if (response.error) setError(true)
      })
      .catch(() => {
        if (mounted) setError(true)
      })
    return () => {
      mounted = false
    }
  }, [open, kind, context])

  const handleItemClick = (linkPath: string) => {
    if (!linkPath) return
    onClose()
    navigate(linkPath)
  }

  let content
  if (error) {
    content = (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Kunne ikke hente data.</Typography>
      </Box>
    )
  } else if (!items) {
    content = (
      <Stack direction="row" sx={{ justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    )
  } else if (items.length === 0) {
    content = (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Ingen treff.
        </Typography>
      </Box>
    )
  } else {
    content = (
      <List dense disablePadding>
        {items.map((item, idx) => {
          const isLinkable = !!item.linkPath
          const rowBg = idx % 2 === 1 ? 'action.hover' : 'transparent'
          return (
            <ListItem
              key={item.id}
              disablePadding
              sx={{
                backgroundColor: rowBg,
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              {isLinkable ? (
                <ListItemButton
                  onClick={() => handleItemClick(item.linkPath)}
                  sx={{ p: 0, '&:hover': { backgroundColor: 'action.selected' } }}
                >
                  <EntityRow item={item} isMobile={isMobile} />
                </ListItemButton>
              ) : (
                <EntityRow item={item} isMobile={isMobile} />
              )}
            </ListItem>
          )
        })}
      </List>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 1 }}>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="span" variant={isMobile ? 'h6' : 'inherit'} sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </Typography>
          <IconButton onClick={onClose} size={isMobile ? 'large' : 'small'} aria-label="Lukk" sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {content}
      </DialogContent>
    </Dialog>
  )
}

export default EntityListDialog
